import { useState } from 'react';
import {
  BarChart3,
  Building2,
  CalendarRange,
  ClipboardList,
  FileText,
  Landmark,
  Layers,
  Recycle,
  ShieldAlert,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { Pestanas, type Pestana } from '@/shared/ui/Pestanas';
import { SelectorDePeriodo } from '@/shared/ui/SelectorDePeriodo';
import { BotonesDeExportacion } from './BotonesDeExportacion';
import { PanelComparativo } from './PanelComparativo';
import { PanelDeCartera } from './PanelDeCartera';
import { PanelDeCatastro } from './PanelDeCatastro';
import { PanelDeVentas } from './PanelDeVentas';
import { PanelDelSri } from './PanelDelSri';
import { PanelDeRecaudacion } from './PanelDeRecaudacion';
import { CONCEPTOS, PanelDeResumen } from './PanelDeResumen';
import { PanelDeServicios } from './PanelDeServicios';
import { PanelDeServiciosEspeciales } from './PanelDeServiciosEspeciales';

function primeroDelMes(): string {
  const hoy = new Date();
  return enIso(new Date(hoy.getFullYear(), hoy.getMonth(), 1));
}

function hoy(): string {
  return enIso(new Date());
}

function enIso(fecha: Date): string {
  const dosCifras = (n: number) => String(n).padStart(2, '0');
  return `${fecha.getFullYear()}-${dosCifras(fecha.getMonth() + 1)}-${dosCifras(fecha.getDate())}`;
}

const PESTANAS = [
  {
    clave: 'resumen',
    rotulo: 'Resumen',
    descripcion: 'Facturado, cobrado y pendiente',
    icono: ClipboardList,
  },
  {
    clave: 'recaudacion',
    rotulo: 'Recaudación',
    descripcion: 'Lo que entró',
    icono: BarChart3,
  },
  {
    clave: 'comparativo',
    rotulo: 'Comparativo',
    descripcion: 'Contra el período anterior',
    icono: TrendingUp,
  },
  {
    clave: 'cartera',
    rotulo: 'Cartera',
    descripcion: 'La deuda de hoy',
    icono: Wallet,
  },
  {
    clave: 'facturacion',
    rotulo: 'Facturación',
    descripcion: 'Qué llegó al SRI',
    icono: FileText,
  },
  {
    clave: 'servicios',
    rotulo: 'Servicios',
    descripcion: 'Qué se facturó de cada uno',
    icono: Layers,
  },
  {
    clave: 'especiales',
    rotulo: 'Especiales',
    descripcion: 'Pesajes del Art. 9 b, c y d',
    icono: Recycle,
  },
  {
    clave: 'ventas',
    rotulo: 'Ventas',
    descripcion: 'Para la declaración',
    icono: Landmark,
  },
  {
    clave: 'catastro',
    rotulo: 'Catastro',
    descripcion: 'El padrón por parroquia',
    icono: Building2,
  },
] as const satisfies ReadonlyArray<Pestana<string>>;

type Clave = (typeof PESTANAS)[number]['clave'];

const INFORMES = {
  resumen: [
    {
      rotulo: 'Mensual',
      ruta: '/informes/facturacion/resumen/mensual',
      nombre: 'resumen-mensual',
    },
    {
      rotulo: 'Ruralidad',
      ruta: '/informes/facturacion/resumen/ruralidad',
      nombre: 'resumen-por-ruralidad',
    },
  ],
  recaudacion: [
    { rotulo: 'Mensual', ruta: '/informes/recaudacion/mensual', nombre: 'recaudacion-mensual' },
    {
      rotulo: 'Por cajero',
      ruta: '/informes/recaudacion/por-cajero',
      nombre: 'recaudacion-por-cajero',
    },
    {
      rotulo: 'Por concepto',
      ruta: '/informes/recaudacion/por-concepto',
      nombre: 'recaudacion-por-concepto',
    },
  ],
  comparativo: [{ ruta: '/informes/recaudacion/mensual', nombre: 'recaudacion-mensual' }],
  cartera: [
    {
      rotulo: 'Por antigüedad',
      ruta: '/informes/facturacion/cartera',
      nombre: 'cartera-por-antiguedad',
    },
  ],
  facturacion: [{ ruta: '/informes/facturacion/sri', nombre: 'facturacion-sri' }],
  // Por ahora se ve en pantalla; el PDF y el Excel van detras.
  servicios: [],
  especiales: [],
  ventas: [
    { rotulo: 'Ventas', ruta: '/informes/facturacion/ventas', nombre: 'ventas-sri' },
    {
      rotulo: 'Notas de crédito',
      ruta: '/informes/facturacion/notas-de-credito',
      nombre: 'notas-de-credito',
    },
  ],
  catastro: [
    {
      rotulo: 'Por parroquia',
      ruta: '/informes/catastro/por-parroquia',
      nombre: 'catastro-por-parroquia',
    },
    {
      rotulo: 'Exonerados',
      ruta: '/informes/catastro/exonerados',
      nombre: 'exonerados-por-parroquia',
      aviso:
        'EL LISTADO DE EXONERADOS LLEVA NOMBRES, CÉDULAS Y EL MOTIVO —QUE PUEDE SER UNA' +
        ' DISCAPACIDAD—: ENTRÉGUELO SÓLO A QUIEN DEBA REVISARLO.',
    },
    {
      rotulo: 'Inactivas',
      ruta: '/informes/catastro/cuentas-inactivas',
      nombre: 'cuentas-inactivas',
    },
  ],
} as const satisfies Record<
  Clave,
  ReadonlyArray<{ rotulo?: string; ruta: string; nombre: string; aviso?: string }>
>;

export function PaginaReportes() {
  const [pestana, setPestana] = useState<Clave>('resumen');
  const [concepto, setConcepto] = useState('');
  const [desde, setDesde] = useState(primeroDelMes());
  const [hasta, setHasta] = useState(hoy());

  const conPeriodo = pestana !== 'cartera' && pestana !== 'catastro';

  const informes: ReadonlyArray<{ rotulo?: string; ruta: string; nombre: string; aviso?: string }> =
    INFORMES[pestana];
  const avisos = informes.flatMap((informe) => (informe.aviso ? [informe.aviso] : []));

  return (
    <>
      <EncabezadoDePagina
        icono={BarChart3}
        color="violeta"
        titulo="Reportes"
        subtitulo="Se ven en pantalla y salen en PDF o Excel"
      />

      <Pestanas
        pestanas={PESTANAS}
        activa={pestana}
        alElegir={setPestana}
        etiqueta="Tipo de informe"
      />

      <div className="mb-4 rounded-2xl border border-armazon-200 bg-white p-4">
        <div className="flex flex-wrap items-center gap-3">
          <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-armazon-600">
            <CalendarRange className="size-4 text-armazon-400" aria-hidden />
            Período
          </span>

          <SelectorDePeriodo
            desde={desde}
            hasta={hasta}
            alCambiar={(a, b) => {
              setDesde(a);
              setHasta(b);
            }}
            desactivado={!conPeriodo}
          />

          {(pestana === 'resumen' || pestana === 'recaudacion') && (
            <select
              value={concepto}
              onChange={(e) => setConcepto(e.target.value)}
              aria-label="Concepto"
              className="h-9 rounded-lg border border-armazon-200 bg-white px-2 text-xs uppercase text-armazon-700"
            >
              {CONCEPTOS.map((c) => (
                <option key={c.valor} value={c.valor}>
                  {c.etiqueta}
                </option>
              ))}
            </select>
          )}

          {!conPeriodo && (
            <span className="text-[11px] uppercase text-armazon-400">
              Este informe es una foto de hoy y no usa el período
            </span>
          )}

          <span className="ml-auto flex flex-wrap items-center gap-4">
            {informes.map((informe) => (
              <BotonesDeExportacion
                key={informe.nombre}
                {...(informe.rotulo === undefined ? {} : { rotulo: informe.rotulo })}
                ruta={informe.ruta}
                nombre={informe.nombre}
                parametros={
                  !conPeriodo
                    ? {}
                    : (pestana === 'resumen' || informe.nombre === 'recaudacion-por-cajero') &&
                        concepto
                      ? { desde, hasta, concepto }
                      : { desde, hasta }
                }
              />
            ))}
          </span>
        </div>

        {avisos.length > 0 && (
          <p className="mt-3 flex items-start gap-2 border-t border-armazon-100 pt-3 text-[11px] text-amber-700">
            <ShieldAlert className="mt-px size-3.5 shrink-0" aria-hidden />
            {avisos.join(' ')}
          </p>
        )}
      </div>

      {pestana === 'resumen' ? (
        <PanelDeResumen desde={desde} hasta={hasta} concepto={concepto} />
      ) : pestana === 'recaudacion' ? (
        <PanelDeRecaudacion desde={desde} hasta={hasta} concepto={concepto} />
      ) : pestana === 'comparativo' ? (
        <PanelComparativo desde={desde} hasta={hasta} />
      ) : pestana === 'ventas' ? (
        <PanelDeVentas desde={desde} hasta={hasta} />
      ) : pestana === 'cartera' ? (
        <PanelDeCartera />
      ) : pestana === 'servicios' ? (
        <PanelDeServicios desde={desde} hasta={hasta} />
      ) : pestana === 'especiales' ? (
        <PanelDeServiciosEspeciales desde={desde} hasta={hasta} />
      ) : pestana === 'facturacion' ? (
        <PanelDelSri desde={desde} hasta={hasta} />
      ) : (
        <PanelDeCatastro />
      )}
    </>
  );
}
